"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import {
  DEFAULT_DELETION_DAYS_REMAINING,
  ACCOUNT_DELETION_CARD_TITLE_PREFIX,
  ACCOUNT_DELETION_CARD_BADGE,
} from "./account-deletion-scheduled-card.constants";
import type { AccountDeletionScheduledCardProps } from "./account-deletion-scheduled-card.types";

export function AccountDeletionScheduledCard({
  className,
  daysRemaining = DEFAULT_DELETION_DAYS_REMAINING,
  onCancelDelete,
  isCancelling = false,
  onLogout,
}: AccountDeletionScheduledCardProps) {
  return (
    <div
      className={cn(
        "relative mx-auto w-full max-w-lg rounded-3xl border-2 border-black bg-white p-6 text-center shadow-[8px_8px_0px_#000000] transition-all sm:p-10",
        className
      )}
    >
      {/* Top accent badge */}
      <div className="mb-4 flex items-center justify-center gap-2 text-[#ED5AA9]">
        <span className="text-xl font-bold leading-none">→</span>
        <span className="text-sm font-bold uppercase tracking-wider">
          {ACCOUNT_DELETION_CARD_BADGE}
        </span>
      </div>

      {/* Main Title */}
      <h2 className="mb-3 text-center text-2xl font-bold leading-tight text-[#101828] sm:text-3xl">
        {ACCOUNT_DELETION_CARD_TITLE_PREFIX} {daysRemaining} {daysRemaining === 1 ? "day" : "days"}.
      </h2>

      {/* Description */}
      <p className="mx-auto mb-8 max-w-md text-center text-sm leading-relaxed text-[#475467] sm:text-base">
        Your creator profile and links are currently hidden. You can cancel this
        deletion anytime within 30 days to restore your profile and retain your
        links, audience, and earnings.
      </p>

      {/* Action buttons */}
      <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
        <Button
          type="button"
          onClick={onCancelDelete}
          disabled={isCancelling}
          className="h-12 w-full rounded-xl border-2 border-black bg-[#0D41E1] text-base font-semibold text-white shadow-[3px_3px_0px_#000000] hover:bg-[#0B35B8] active:translate-x-0.5 active:translate-y-0.5 sm:w-auto sm:min-w-[180px] sm:px-8"
        >
          {isCancelling ? "Cancelling..." : "Cancel delete"}
        </Button>

        {onLogout && (
          <Button
            type="button"
            variant="outline"
            onClick={onLogout}
            disabled={isCancelling}
            className="h-12 w-full rounded-xl border-2 border-black bg-white text-base font-semibold text-[#344054] shadow-[3px_3px_0px_#000000] hover:bg-gray-50 active:translate-x-0.5 active:translate-y-0.5 sm:w-auto sm:min-w-[130px] sm:px-6"
          >
            Log out
          </Button>
        )}
      </div>
    </div>
  );
}

export type { AccountDeletionScheduledCardProps };
